import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { EditTicketAndPassModal } from "Bands/containers";
import { dataMetaSelector, editEventTicketAndPass } from "Bands/state";
import { BAND_ACTIONS, IGuestlistTicketAndPass, IGuestlistTicketAndPassParams } from "Bands/types";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IJSONAPIErrorCodes, IStatus } from "shared/types";
import { useAPIError, useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";

const editCopy = copy.guestlistBlock.ticketAndPasses.edit;

interface EditTicketAndPassProps {
  blockId: string;
  ticketAndPass: IGuestlistTicketAndPass;
  handleClose: () => void;
}

export const EditTicketAndPass: FC<EditTicketAndPassProps> = ({
  blockId,
  ticketAndPass,
  handleClose,
}) => {
  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editEventTicketAndPass));
  useClearActionMetaOnUnMount(BAND_ACTIONS.editEventTicketAndPass);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      handleClose();

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.messages.success,
        })
      );
    }
  }, [apiMeta?.status, blockId, dispatch, handleClose]);

  const ticketAndPassParams = useMemo(
    () => ({
      ...ticketAndPass,
      ticketAndPassType: ticketAndPass?.ticketAndPassType.id,
      ticketsTotal: ticketAndPass?.isUnlimited
        ? ticketAndPass.ticketsDistributed
        : ticketAndPass?.ticketsTotal,
    }),
    [ticketAndPass]
  );

  const handleEdit = (data: IGuestlistTicketAndPassParams, id: string) => {
    const { isUnlimited, ticketsTotal, ...rest } = data;

    const cleanData: IGuestlistTicketAndPassParams = {
      ...rest,
      isUnlimited: !!isUnlimited,
      ticketsTotal: isUnlimited ? null : ticketsTotal,
    };

    dispatch(
      editEventTicketAndPass({
        id,
        data: cleanData,
        blockId,
      })
    );
  };

  const { error } = useAPIError({
    apiError: apiMeta?.error,
    message: editCopy.messages.nbGuestsError,
    errorCode: IJSONAPIErrorCodes.NumericTooLowError,
  });

  return (
    <EditTicketAndPassModal
      ticketAndPass={ticketAndPassParams}
      handleEdit={handleEdit}
      handleClose={handleClose}
      apiMeta={apiMeta}
      error={error}
    />
  );
};
